import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { UserRole } from '@/types';
import { supabase } from '@/integrations/supabase/client';
import type { Session } from '@supabase/supabase-js';
import { track } from '@/lib/telemetry';

export type AuthMode = 'demo' | 'real';

interface AuthState {
  isAuthenticated: boolean;
  mode: AuthMode | null;
  role: UserRole | null;
  userName: string;
  userId: string;
  dealershipId: string | null;
  dealershipName: string;
  isSolo: boolean;
  loading: boolean;
}

interface AuthContextType extends AuthState {
  loginDemo: (role: UserRole) => void;
  signInWithPassword: (email: string, password: string) => Promise<{ error: string | null }>;
  signUpWithPassword: (email: string, password: string, fullName: string) => Promise<{ error: string | null }>;
  resetPassword: (email: string) => Promise<{ error: string | null }>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

const demoDefaults: Record<UserRole, { name: string; id: string }> = {
  rep: { name: 'Alex Carter', id: 'rep-alex' },
  manager: { name: 'Sarah Manager', id: 'mgr-001' },
  admin: { name: 'Admin User', id: 'admin-001' },
};

const initialState: AuthState = {
  isAuthenticated: false,
  mode: null,
  role: null,
  userName: '',
  userId: '',
  dealershipId: null,
  dealershipName: '',
  isSolo: false,
  loading: true,
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [auth, setAuth] = useState<AuthState>(initialState);

  // Hydrate session for Real mode
  useEffect(() => {
    let active = true;

    const loadProfile = async (session: Session | null) => {
      if (!session?.user) {
        if (active) setAuth({ ...initialState, loading: false });
        return;
      }
      // Defer DB calls to avoid deadlock inside auth callback
      setTimeout(async () => {
        if (!active) return;
        const userId = session.user.id;
        const { data: profile } = await supabase
          .from('profiles')
          .select('full_name, email, dealership_id, dealerships(name, is_solo, owner_user_id)')
          .eq('id', userId)
          .maybeSingle();
        const { data: roles } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', userId);
        // Prefer manager > admin > rep when user has multiple roles (solo users have both manager + rep)
        const roleSet = new Set((roles ?? []).map(r => r.role as UserRole));
        const role: UserRole = roleSet.has('admin')
          ? 'admin'
          : roleSet.has('manager')
            ? 'manager'
            : 'rep';
        const dealership = (profile as any)?.dealerships;
        if (!active) return;
        setAuth({
          isAuthenticated: true,
          mode: 'real',
          role,
          userName: profile?.full_name || session.user.email || '',
          userId,
          dealershipId: profile?.dealership_id ?? null,
          dealershipName: dealership?.name ?? '',
          isSolo: !!dealership?.is_solo,
          loading: false,
        });
        // Fire a once-per-day "app_open" ping so we can compute DAU/WAU reliably.
        try {
          const key = `po_app_open_${userId}_${new Date().toISOString().slice(0, 10)}`;
          if (!localStorage.getItem(key)) {
            localStorage.setItem(key, '1');
            track('app_open', { userId, dealershipId: profile?.dealership_id ?? null, mode: 'real' }, { role });
          }
        } catch { /* ignore storage errors */ }
      }, 0);
    };

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        loadProfile(session);
      } else {
        // Only clear if we're not in demo mode (use functional update to avoid stale closure)
        setAuth(prev => (prev.mode === 'demo' ? prev : { ...initialState, loading: false }));
      }
    });

    supabase.auth.getSession().then(({ data }) => loadProfile(data.session));

    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loginDemo = (role: UserRole) => {
    const d = demoDefaults[role];
    setAuth({
      isAuthenticated: true,
      mode: 'demo',
      role,
      userName: d.name,
      userId: d.id,
      dealershipId: 'demo-dealership',
      dealershipName: 'My Dealership',
      isSolo: false,
      loading: false,
    });
  };

  const signInWithPassword = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error?.message ?? null };
  };

  const signUpWithPassword = async (email: string, password: string, fullName: string) => {
    const redirectUrl = `${window.location.origin}/dashboard`;
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: redirectUrl, data: { full_name: fullName } },
    });
    return { error: error?.message ?? null };
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { error: error?.message ?? null };
  };

  const logout = async () => {
    if (auth.mode === 'real') await supabase.auth.signOut();
    setAuth({ ...initialState, loading: false });
  };

  const refreshProfile = async () => {
    const { data } = await supabase.auth.getSession();
    if (!data.session) return;
    const userId = data.session.user.id;
    const { data: profile } = await supabase
      .from('profiles')
      .select('full_name, email, dealership_id, dealerships(name, is_solo, owner_user_id)')
      .eq('id', userId)
      .maybeSingle();
    const dealership = (profile as any)?.dealerships;
    setAuth(prev => ({
      ...prev,
      dealershipName: dealership?.name ?? prev.dealershipName,
      isSolo: !!dealership?.is_solo,
    }));
  };

  return (
    <AuthContext.Provider
      value={{ ...auth, loginDemo, signInWithPassword, signUpWithPassword, resetPassword, logout, refreshProfile }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
